"use strict";

(() => {
	class Savings_Calculator {
		/** @var {HTMLElement} */
		#$widget;

		/** @var {HTMLInputElement} */
		#$initialDepositInput;

		/** @var {HTMLInputElement} */
		#$monthlyDepositInput;

		/** @var {HTMLInputElement} */
		#$horizonInput;

		/** @var {HTMLInputElement} */
		#$interestInput;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$interestTaxInputs;


		/** @var {HTMLElement} */
		#$resultSentenceTotalSavings;

		/** @var {HTMLElement} */
		#$resultSentenceTotalInterest;


		/** @var {HTMLCanvasElement} */
		#$resultChart;

		/** @var {Chart} */
		#resultChart;


		/** @var {HTMLTableElement} */
		#$resultTable;

		/** @var {HTMLTableSectionElement} */
		#$resultTableBody;

		/** @var {HTMLTemplateElement} */
		#$resultTableRowTemplate;


		constructor($widget) {
			this.#$widget = $widget;
		}

		initialize() {
			// Initialize selectors.
			this.#$initialDepositInput = this.#$widget.querySelector(".dan-input-initial-deposit");
			this.#$monthlyDepositInput = this.#$widget.querySelector(".dan-input-monthly-deposit");
			this.#$horizonInput        = this.#$widget.querySelector(".dan-input-horizon");
			this.#$interestInput       = this.#$widget.querySelector(".dan-input-interest");
			this.#$interestTaxInputs   = this.#$widget.querySelectorAll(".dan-inputs-interest-tax input[type='radio']");

			this.#$resultSentenceTotalSavings  = this.#$widget.querySelector(".dan-result-sentence-total");
			this.#$resultSentenceTotalInterest = this.#$widget.querySelector(".dan-result-sentence-interest");

			this.#$resultChart = this.#$widget.querySelector(".dan-result-chart");

			this.#$resultTable             = this.#$widget.querySelector(".dan-result-table");
			this.#$resultTableBody         = this.#$resultTable.querySelector("tbody");
			this.#$resultTableRowTemplate  = this.#$resultTableBody.querySelector("template");

			// Add handlers to events.
			this.#$initialDepositInput.addEventListener("input", (this.refresh).bind(this));
			this.#$monthlyDepositInput.addEventListener("input", (this.refresh).bind(this));
			this.#$horizonInput.addEventListener("input", (this.refresh).bind(this));
			this.#$interestInput.addEventListener("input", (this.refresh).bind(this));

			for (const $input of this.#$interestTaxInputs) {
				$input.addEventListener("change", (this.refresh).bind(this));
			}

			// Initialize chart.
			this.#initializeChart();

			// Initialize tooltips.
			const $tooltips = this.#$widget.querySelectorAll("[data-tooltip]");

			if ($tooltips.length > 0) {
				// noinspection JSUnresolvedReference
				tippy($tooltips, {
					content: ($element) => $element.getAttribute("data-tooltip"),
					allowHTML: true
				});
			}

			// Refresh result.
			this.refresh();
		}

		#initializeChart() {
			this.#resultChart = new Chart(this.#$resultChart, {
				type: "line",
				data: {},
				options: {
					interaction: {
						mode: "index",
						intersect: false
					},
					scales: {
						x: {
							beginAtZero: true
						},
						ySavings: {
							type: "linear",
							position: "left",
							beginAtZero: true,
							ticks: {
								callback: (value) => {
									return Dan_Utilities.formatPrice(value) + " Kč";
								}
							}
						},
						yInterest: {
							type: "linear",
							position: "right",
							beginAtZero: true,
							ticks: {
								callback: (value) => {
									return Dan_Utilities.formatPrice(value) + " Kč";
								}
							},
							grid: {
								drawOnChartArea: false
							}
						}
					},
					plugins: {
						legend: {
							onClick: null,
							labels: {
								usePointStyle: true,
								pointStyle: "circle",
								boxWidth: 6,
								boxHeight: 6
							}
						},
						tooltip: {
							displayColors: false,
							callbacks: {
								title: (contexts) => {
									return (contexts[0].dataIndex + 1) + ". rok";
								},
								label: (context) => {
									let label;
									label = context.dataset.label + ": ";
									label += Dan_Utilities.formatPrice(context.raw) + " Kč";

									return label;
								}
							}
						}
					}
				}
			});
		}

		refresh() {
			const inputs = this.getInputs();

			if (
				inputs.initialDeposit === null
				|| inputs.monthlyDeposit === null
				|| inputs.horizon === null
				|| inputs.interest === null
			) {
				this.#setResult(null);
				return;
			}

			const result = this.#calcResult(
				inputs.initialDeposit,
				inputs.monthlyDeposit,
				inputs.horizon,
				inputs.interest,
				inputs.interestTax
			);

			this.#setResult(result);
		}

		#calcResult(
			initialDeposit,
			monthlyDeposit,
			years,
			interest,
			interestTax
		) {
			const data = {
				totalSavings: initialDeposit,
				totalInterest: 0,
				years: {}
			};

			const interestRate = (interest / 100);

			for (let year = 1; year <= years; year++) {
				// Add monthly investments to total savings.
				data.totalSavings += (monthlyDeposit * 12);

				// Calc interest.
				const interest = (interestTax)
					? (data.totalSavings * interestRate * 0.85)
					: (data.totalSavings * interestRate);

				// Add interest to total savings.
				data.totalSavings  += interest;
				data.totalInterest += interest;

				// Save yearly data.
				data.years[year] = {
					savings: data.totalSavings,
					interest: data.totalInterest
				};
			}

			return data;
		}

		#setResult(data) {
			this.#setSentenceResults(data);
			this.#setChartResults(data);
			this.#setTableResults(data);
		}

		#setSentenceResults(data) {
			if (data === null) {
				this.#$resultSentenceTotalSavings.textContent  = "-";
				this.#$resultSentenceTotalInterest.textContent = "-";
				return;
			}

			const totalSavingsFormat  = Dan_Utilities.formatPrice(data.totalSavings);
			const totalInterestFormat = Dan_Utilities.formatPrice(data.totalInterest);

			this.#$resultSentenceTotalSavings.textContent  = totalSavingsFormat + " Kč";
			this.#$resultSentenceTotalInterest.textContent = totalInterestFormat + " Kč";
		}

		#setChartResults(data) {
			if (data === null) {
				// noinspection JSUndefinedPropertyAssignment
				this.#resultChart.data = {};

				// noinspection JSUnresolvedReference
				this.#resultChart.update("none");

				return;
			}

			const dataYears    = [];
			const dataSavings  = [];
			const dataInterest = [];

			for (const year in data.years) {
				dataYears.push(year);
				dataSavings.push(data.years[year].savings);
				dataInterest.push(data.years[year].interest);
			}

			// noinspection JSUndefinedPropertyAssignment
			this.#resultChart.data = {
				labels: dataYears,
				datasets: [
					{
						label: "Naspořená částka",
						data: dataSavings,
						yAxisID: "ySavings",
						borderColor: "#292929",
						borderWidth: 2,
						backgroundColor: "#292929"
					},
					{
						label: "Z toho úrok",
						data: dataInterest,
						yAxisID: "yInterest",
						borderColor: "#DAFB54",
						borderWidth: 2,
						backgroundColor: "#DAFB54"
					}
				]
			};

			// noinspection JSUnresolvedReference
			this.#resultChart.update("none");
		}

		#setTableResults(data) {
			// Empty table body.
			const $currentRows = this.#$resultTableBody.querySelectorAll("tr");

			for (const $row of $currentRows) {
				$row.remove();
			}

			// Do not do anything else if data is null.
			if (data === null) {
				return;
			}

			// Add row for each year.
			for (const year in data.years) {
				const $row     = document.createElement("tr");
				$row.innerHTML = this.#$resultTableRowTemplate.innerHTML;

				const yearFormat     = year + ".";
				const savingsFormat  = Dan_Utilities.formatPrice(data.years[year].savings) + " Kč";
				const interestFormat = Dan_Utilities.formatPrice(data.years[year].interest) + " Kč";

				$row.querySelector(".dan-result-table-row-year").textContent     = yearFormat;
				$row.querySelector(".dan-result-table-row-total").textContent    = savingsFormat;
				$row.querySelector(".dan-result-table-row-interest").textContent = interestFormat;

				this.#$resultTableBody.appendChild($row);
			}
		}

		getInputs() {
			// Initial deposit.
			let initialDeposit;
			initialDeposit = this.#$initialDepositInput.value || this.#$initialDepositInput.placeholder;
			initialDeposit = parseInt(initialDeposit);

			if (isNaN(initialDeposit) || initialDeposit < 0) {
				initialDeposit = null;
			}

			// Monthly deposit.
			let monthlyDeposit;
			monthlyDeposit = this.#$monthlyDepositInput.value || this.#$monthlyDepositInput.placeholder;
			monthlyDeposit = parseInt(monthlyDeposit);

			if (isNaN(monthlyDeposit) || monthlyDeposit < 0) {
				monthlyDeposit = null;
			}

			// Horizon.
			let horizon;
			horizon = this.#$horizonInput.value || this.#$horizonInput.placeholder;
			horizon = parseInt(horizon);

			if (isNaN(horizon) || horizon < 1) {
				horizon = null;
			}

			// Inrerest.
			let interest;
			interest = this.#$interestInput.value || this.#$interestInput.placeholder;
			interest = parseFloat(interest);

			if (!isNaN(interest) && interest >= 0) {
				interest = (Math.round(interest * 100) / 100);
			} else {
				interest = null;
			}

			// Interest tax.
			let interestTax = null;

			for (const $input of this.#$interestTaxInputs) {
				if ($input.checked) {
					interestTax = $input.value;
					break;
				}
			}

			interestTax = (interestTax === "yes");

			// Return.
			return {
				initialDeposit,
				monthlyDeposit,
				horizon,
				interest,
				interestTax
			};
		}

		setInputs(
			initialDeposit,
			monthlyDeposit,
			horizon,
			interest,
			interestTax
		) {
			this.#$initialDepositInput.value = initialDeposit;
			this.#$monthlyDepositInput.value = monthlyDeposit;
			this.#$horizonInput.value        = horizon;
			this.#$interestInput.value       = interest;

			const interestTaxValue = (interestTax ? "yes" : "no");

			for (const $input of this.#$interestTaxInputs) {
				$input.checked = ($input.value === interestTaxValue);
			}
		}
	}

	// Initialize widgets.
	const $widgets = document.querySelectorAll(".dan-savings-calculator");

	for (const $widget of $widgets) {
		$widget.calculator = new Savings_Calculator($widget);
		$widget.calculator.initialize();
	}
})();
